import { Component, inject, OnInit, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { CreateBotDialogComponent } from './create-bot-dialog.component';
import { EditBotDialogComponent } from './edit-bot-dialog.component';
import { DeleteBotDialogComponent } from './delete-bot-dialog.component';
import { DevicesService } from './devices.service';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router'


export interface Camera {
  id: string;
  name: string;
  deviceId: string;
}

export interface Device {
  id: string;
  name: string;
  status: 'online' | 'offline' | 'deactivated';
  cockpits: Camera[];
  mainPhotoUrl: string;
  layoutId:string;
}

@Component({
  selector: 'app-bots',
  templateUrl: './bots.component.html',
  styleUrls: ['./bots.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatCardModule,
  ],
  standalone: true,
})
export class DevicesComponent implements OnInit {
  private dialog = inject(MatDialog);
  private devicesService = inject(DevicesService);
  private router = inject(Router)

  // Signals from service
  devices = this.devicesService.devices;
  onlineDevices = this.devicesService.onlineDevices;
  isLoading = this.devicesService.isLoading;
  error = this.devicesService.error;

  displayedDevices = signal<Device[]>([]);
  filteredDevices:Device[] = []

  // Pagination
  pageSize = 5;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25, 50];

  // Filter
  statusFilter: string = 'all';

  constructor() {
    // Effect to update filtered and displayed devices when devices signal changes
    effect(() => {

      const onlineDevices = this.onlineDevices();
      const devices = this.devices().map(device => {
        const status = onlineDevices.find(onlineDevice => onlineDevice.id === device.id) ? 'online' as const : 'offline' as const
        return {
          ...device,
          status,
        }
      })
      this.displayedDevices.set(devices)

      console.log('displayedDevices', this.displayedDevices)
      //this.applyFilter();
    });
  }

  ngOnInit(): void {
    this.devicesService.fetchDevices();
  }

  openCreateBotDialog(): void {
    const dialogRef = this.dialog.open(CreateBotDialogComponent, {
      width: '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.submitted) {
        // Reload devices with current filter after creation
        this.devicesService.createDeviceAndReload(result.deviceData, this.statusFilter);
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updateDisplayedDevices();
  }

  onStatusFilterChange(status: string): void {
    this.statusFilter = status;
    this.pageIndex = 0; // Reset to first page when filtering
    this.applyFilter();
  }

  applyFilter(): void {
    this.filteredDevices = this.devicesService.getFilteredDevices(this.statusFilter);
    this.updateDisplayedDevices();
  }

  updateDisplayedDevices(): void {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    if (Array.isArray(this.filteredDevices)) {
      this.displayedDevices.set(this.filteredDevices.slice(startIndex, endIndex));
      console.log('displayedDevices', this.displayedDevices);
    } else {
      this.displayedDevices.set([])
      console.log('displayedDevices', this.displayedDevices);
    }
  }

  onEdit(device: Device): void {
    const dialogRef = this.dialog.open(EditBotDialogComponent, {
      width: '500px',
      data: device,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.submitted) {
        this.devicesService.updateDeviceAndReload(device.id, result.deviceData);
      }
    });
  }

  onDelete(device: Device): void {
    const isMobile = window.innerWidth <= 600;
    const dialogRef = this.dialog.open(DeleteBotDialogComponent, {
      width: isMobile ? '95vw' : '600px',
      maxWidth: '100vw',
      data: device,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.devicesService.deleteDevice(device.id);
      }
    });
  }

  onConnect(device: Device): void {
        this.router.navigate(['./cockpit'], {
      queryParams: { id:device.id, name:device.name },
      queryParamsHandling: 'merge',
    })
      // Handle connection here
  }

  viewMode = signal<'grid' | 'rows'>('rows');

  setViewMode(mode: 'grid' | 'rows'): void {
    this.viewMode.set(mode);
  }

  get onlineCount(): number {
    return this.onlineDevices().length;
  }

  get offlineCount(): number {
    return this.devices().length - this.onlineDevices().length;
  }

  get startItem(): number {
    return this.totalDevices === 0 ? 0 : this.pageIndex * this.pageSize + 1;
  }

  get endItem(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.totalDevices);
  }

  prevPage(): void {
    if (this.pageIndex > 0) {
      this.pageIndex--;
      this.updateDisplayedDevices();
    }
  }

  nextPage(): void {
    if (this.endItem < this.totalDevices) {
      this.pageIndex++;
      this.updateDisplayedDevices();
    }
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = Number((event.target as HTMLSelectElement).value);
    this.pageIndex = 0;
    this.updateDisplayedDevices();
  }

  retryLoad(): void {
    this.devicesService.fetchDevices();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'online':
        return 'status-online';
      case 'offline':
        return 'status-offline';
      case 'deactivated':
        return 'status-deactivated';
      default:
        return '';
    }
  }

  get totalDevices(): number {
    return this.devices().length;
  }

  clearFilter(): void {
    this.statusFilter = 'all';
    this.pageIndex = 0;
    this.applyFilter();
  }
}

